/**
 * Staff session, held separately from the student one.
 *
 * A booth manager and a student could share a device — the help desk laptop,
 * a borrowed phone — so the two sessions never overwrite each other.
 */

import type { UserRole } from '@sdg/shared'

const TOKEN_KEY = 'sdg.staff'
const EXPIRY_KEY = 'sdg.staff.expires'
const PROFILE_KEY = 'sdg.staff.profile'

export interface StaffProfile {
  role: UserRole
  username: string
  name: string | null
  mustChangePassword: boolean
  booth: { boothNo: number; school: string } | null
  /** The panel seat a judge holds — "Judge 1" on the slide-23 matrix. */
  judge: { id: number; label: string; name: string | null } | null
}

export function saveStaffSession(token: string, expiresAt: string, profile: StaffProfile) {
  localStorage.setItem(TOKEN_KEY, token)
  localStorage.setItem(EXPIRY_KEY, expiresAt)
  localStorage.setItem(PROFILE_KEY, JSON.stringify(profile))
}

export function getStaffSession(): string | null {
  const token = localStorage.getItem(TOKEN_KEY)
  const expiry = localStorage.getItem(EXPIRY_KEY)
  if (!token || !expiry) return null

  if (new Date(expiry).getTime() <= Date.now()) {
    clearStaffSession()
    return null
  }
  return token
}

export function getStaffProfile(): StaffProfile | null {
  const raw = localStorage.getItem(PROFILE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as StaffProfile
  } catch {
    return null
  }
}

export function clearStaffSession() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(EXPIRY_KEY)
  localStorage.removeItem(PROFILE_KEY)
}
